import { useEffect, useCallback, useRef } from "react";

const STORAGE_KEY = "marketplace_state";
const SCROLL_RESTORE_KEY = "marketplace_scroll_restore";

interface MarketplaceState {
  viewType: "list" | "grid";
  selectedCategory: string;
  commercialType: string;
  searchQuery: string;
  scrollPosition: number;
  timestamp: number;
}

const DEFAULT_STATE: MarketplaceState = {
  viewType: "list",
  selectedCategory: "all",
  commercialType: "all",
  searchQuery: "",
  scrollPosition: 0,
  timestamp: 0,
};

// 5 minutes expiry for saved state
const STATE_EXPIRY_MS = 5 * 60 * 1000;

export const useMarketplaceState = () => {
  const isRestoring = useRef(false);

  const getSavedState = useCallback((): MarketplaceState | null => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (!saved) return null;
      
      const parsed = JSON.parse(saved) as MarketplaceState;
      
      // Check if state is expired
      if (Date.now() - parsed.timestamp > STATE_EXPIRY_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      
      return parsed;
    } catch {
      return null;
    }
  }, []);

  const saveState = useCallback((state: Partial<MarketplaceState>) => {
    try {
      const current = getSavedState() || DEFAULT_STATE;
      const newState: MarketplaceState = {
        ...current,
        ...state,
        scrollPosition: window.scrollY,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      // Mark that scroll should be restored on return
      sessionStorage.setItem(SCROLL_RESTORE_KEY, "true");
    } catch {
      // Ignore storage errors
    }
  }, [getSavedState]);

  const clearState = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SCROLL_RESTORE_KEY);
  }, []);

  const restoreScrollPosition = useCallback(() => {
    const shouldRestore = sessionStorage.getItem(SCROLL_RESTORE_KEY) === "true";
    if (!shouldRestore) return;

    const saved = getSavedState();
    if (saved && saved.scrollPosition > 0 && !isRestoring.current) {
      isRestoring.current = true;
      // Clear the restore flag immediately to prevent multiple restores
      sessionStorage.removeItem(SCROLL_RESTORE_KEY);
      
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo(0, saved.scrollPosition);
        isRestoring.current = false;
      });
    }
  }, [getSavedState]);

  const shouldSkipScrollToTop = useCallback((): boolean => {
    return sessionStorage.getItem(SCROLL_RESTORE_KEY) === "true";
  }, []);

  const getInitialState = useCallback((): Partial<MarketplaceState> => {
    const saved = getSavedState();
    if (saved) {
      return {
        viewType: saved.viewType,
        selectedCategory: saved.selectedCategory,
        commercialType: saved.commercialType,
        searchQuery: saved.searchQuery,
      };
    }
    return DEFAULT_STATE;
  }, [getSavedState]);

  return {
    getSavedState,
    saveState,
    clearState,
    restoreScrollPosition,
    shouldSkipScrollToTop,
    getInitialState,
  };
};

// Export helper for ScrollToTop component
export const shouldSkipMarketplaceScroll = (): boolean => {
  return sessionStorage.getItem(SCROLL_RESTORE_KEY) === "true";
};
